<script lang="ts">
	import { format } from 'svelte-i18n';
	import { createEventDispatcher, onMount } from 'svelte';
	import type { HomeAvatar } from 'src/types/Home';
	import { ImageGroup, ImageUpload } from '@Shelly/shelly-design-system';
	import type { ImageGroupImageType } from '@Shelly/shelly-design-system';
	import { AvatarColorVariant } from '@Shelly/shelly-design-system/helpers/enums';
	import { getImages, mapImgArray, uploadImage } from '../Lists/list-helpers';
	import { load_image_from } from 'src/helpers/interface_helpers';
	import { imgGroupHousesArr } from '../Profiles/profiles_helpers';

	type AvatarImage = ImageGroupImageType & { filepath?: string };

	export let avatar: HomeAvatar = {};
	export let allowUpload = true;
	export let uploadShape: 'circle' | 'rectangular' = 'circle';

	const dispatch = createEventDispatcher<{ isUploading: boolean }>();
	let selectedImage = '';

	const colorValues = Object.values(AvatarColorVariant).slice(0, -5);
	let imgGroupColors: ImageGroupImageType[] = [...Array(colorValues.length).keys()].map((_, i) => ({
		backgroundColor: colorValues[i] as ImageGroupImageType['backgroundColor'],
		id: i,
		selected: false,
		type: 'color',
	}));

	let imgGroupHouses: ImageGroupImageType[] = [...imgGroupHousesArr];
	let uploadedImages: AvatarImage[] = [];
	let allImages: AvatarImage[] = [];

	const syncSelection = (): void => {
		allImages = [...imgGroupColors, ...imgGroupHouses, ...uploadedImages].map((img) => {
			const matchImage = !!(img.image && avatar?.image && img.image === avatar.image);
			const matchColor = !!(
				img.backgroundColor &&
				avatar?.backgroundColor &&
				img.backgroundColor === avatar.backgroundColor
			);
			return { ...img, selected: matchImage || matchColor };
		});

		if (allImages.filter((img) => img.selected).length > 1) {
			allImages = allImages.map((img) =>
				img.type === 'color' ? { ...img, selected: false } : img,
			);
		}

		imgGroupColors = allImages.filter((img) => img.type === 'color');
		imgGroupHouses = allImages.filter((img) => img.type === 'house');
		uploadedImages = allImages.filter((img) => img.type === 'uploaded');
	};

	const onSelectImg = (e: CustomEvent): void => {
		const { id, type } = e.detail;
		const selected = allImages.find((img) => img.id === id && img.type === type);
		if (!selected) return;

		if (selected.image) {
			avatar = { ...avatar, image: selected.image, backgroundColor: undefined };
		} else if (selected.backgroundColor) {
			avatar = { ...avatar, backgroundColor: selected.backgroundColor, image: undefined };
		}

		syncSelection();
	};

	const onUploadImage = async (e): Promise<void> => {
		if (!allowUpload) return;

		dispatch('isUploading', true);
		const uploaded = await uploadImage(e, 'avatar');
		if (Array.isArray(uploaded) && uploaded[0]) {
			selectedImage = uploaded[0].filepath;
			const imageUrl = load_image_from(selectedImage);

			uploadedImages = [
				{
					filepath: uploaded[0].filepath,
					image: imageUrl,
					id: 0,
					selected: true,
					type: 'uploaded',
				},
			];

			avatar = { ...avatar, image: imageUrl, backgroundColor: undefined };
			syncSelection();
		}

		e.target.value = '';
		dispatch('isUploading', false);
	};

	onMount(async () => {
		const fetched = await getImages('avatar');
		uploadedImages = mapImgArray(fetched, 'uploaded', selectedImage);
		syncSelection();
	});
</script>

<div class:avatar-picker-upload-rectangular={uploadShape === 'rectangular'}>
	{#if allowUpload}
		<ImageGroup
			class="mb-m"
			title={$format('title_uploaded_pictures', { default: 'Uploaded pictures' })}
			images={uploadedImages}
			single={true}
			on:click={onSelectImg}
		>
			<ImageUpload
				slot="imgUpload"
				class={uploadShape === 'rectangular' ? 'upload-shape-rectangular' : ''}
				accept=".jpg, .jpeg, .heif, .png"
				on:change={onUploadImage}
				type={'circle'}
				btnSettings={null}
			/>
		</ImageGroup>

		<ImageGroup
			class="mb-m"
			title={$format('title_default_pictures', { default: 'Default pictures' })}
			images={imgGroupHouses}
			on:click={onSelectImg}
		/>
	{/if}

	<ImageGroup
		class="mb-s"
		title={$format('image_group_title_colors_gradients', { default: 'Colors and Gradients' })}
		images={imgGroupColors}
		on:click={onSelectImg}
	/>
</div>

<style>
	.avatar-picker-upload-rectangular :global(.upload-shape-rectangular.image-upload.type-circle) {
		border-radius: var(--radius-big);
	}

	.avatar-picker-upload-rectangular
		:global(.upload-shape-rectangular.image-upload.type-circle::before) {
		border-radius: var(--radius-big);
	}

	.avatar-picker-upload-rectangular :global(.upload-shape-rectangular.image-upload .image-dash) {
		display: none;
	}

	.avatar-picker-upload-rectangular
		:global(.upload-shape-rectangular.image-upload.type-circle:not(.disabled)) {
		box-shadow: inset 0 0 0 2px var(--color-primary);
	}

	.avatar-picker-upload-rectangular
		:global(.upload-shape-rectangular.image-upload.type-circle.disabled) {
		box-shadow: inset 0 0 0 2px var(--color-3);
	}
</style>
